import {
  createHTTP,
  HTTPEndpointInput,
  HTTPError,
  HTTPRequestJSONOptions,
  HTTPRequestOptions,
} from '@superdispatch/http';
import { URITemplateParams } from '@superdispatch/uri';
import { get } from 'es-toolkit/compat';
import { createAPIError } from 'shared/api/APIError';
import { getAuthToken } from 'shared/auth/AuthToken';
import { REPORTING_HOST } from 'shared/constants/ServerConstants';
import { emitServerError } from 'shared/errors/ServerErrorEvents';
import { logWarning } from 'shared/helpers/Logger';

export interface ReportingAPIResponse<TData = unknown> {
  data: TData;
}

interface ReportingAPIErrorResponse {
  status: 'fail';
  data: { message: string; error_id: string; details: unknown };
}

export function isReportingAPIErrorResponse(
  response: unknown,
): response is ReportingAPIErrorResponse {
  return (
    typeof response === 'object' &&
    response != null &&
    !!get(response, 'data') &&
    !!get(response, 'status')
  );
}

export function getReportingRequestHeaders(
  headers: Record<string, string> = {},
) {
  headers = { ...headers };

  if (!headers.authorization) {
    const token = getAuthToken();
    if (token) {
      headers.authorization = `Token ${token}`;
    }
  }

  return headers;
}

const reportingHttpClient = createHTTP({
  baseURL: REPORTING_HOST,
  headers(headers) {
    return getReportingRequestHeaders(headers as Record<string, string>);
  },
});

function sanitizeSecret(secret: string | undefined) {
  if (secret == null) {
    return undefined;
  }

  return secret.substring(0, 4) + '****' + secret.substring(secret.length - 4);
}

function handleError(input: unknown): (error: unknown) => Promise<never> {
  return async (errorResponse: unknown) => {
    let error: Error;

    if (errorResponse instanceof HTTPError) {
      const { status, statusText, url } = errorResponse.response;
      let errorJson;

      try {
        errorJson = (await errorResponse.response.json()) as HTTPError;
      } catch (e: unknown) {}

      if (isReportingAPIErrorResponse(errorJson)) {
        error = createAPIError({
          ...errorResponse,
          requestParams: input,
          status: errorResponse.response.status,
          context: errorJson.data.details,
          type: errorJson.data.error_id,
          message: errorJson.data.message,
        });
      } else {
        const host = url ? new URL(url).hostname : 'api';
        error = createAPIError({
          ...errorResponse,
          requestParams: input,
          status,
          message: `${host} failed (${status} ${statusText})`,
        });
      }
    } else if (errorResponse instanceof Error) {
      const isTimeout = /timeout|abort/i.test(
        errorResponse.name + errorResponse.message,
      );
      const prefix = isTimeout ? 'Timeout:' : 'Network:';

      error = createAPIError({
        message: `${prefix} ${errorResponse.message}`,
        requestParams: input,
        context: { originalError: errorResponse, isTimeout },
      });
    } else {
      error = createAPIError({
        message: 'Unknown Error',
        context: errorResponse,
        requestParams: input,
      });
    }

    const isUnauthorized =
      errorResponse instanceof HTTPError &&
      errorResponse.response.status === 401;
    logWarning(`APIError: ${error.message}`, {
      error,
      response: errorResponse,
      hasToken: !!getAuthToken(),
      authToken: isUnauthorized ? sanitizeSecret(getAuthToken()) : undefined,
    });

    emitServerError(error);

    return Promise.reject(error);
  };
}

function requestJSON<
  TData,
  TParams extends URITemplateParams = URITemplateParams,
>(
  input: HTTPEndpointInput<TParams>,
  options?: HTTPRequestJSONOptions<TData>,
): Promise<TData> {
  return reportingHttpClient
    .requestJSON(input, options)
    .catch(handleError(input));
}

export function requestJSONReportingAPI<
  TData extends ReportingAPIResponse,
  TParams extends URITemplateParams = URITemplateParams,
>(
  input: HTTPEndpointInput<TParams>,
  options?: Omit<HTTPRequestJSONOptions<TData>, 'baseURL'>,
): Promise<TData> {
  return requestJSON(input, options);
}

export function requestReportingAPI<
  TParams extends URITemplateParams = URITemplateParams,
>(
  input: HTTPEndpointInput<TParams>,
  options?: Omit<HTTPRequestOptions, 'baseURL'>,
): Promise<Response> {
  return reportingHttpClient.request(input, options).catch(handleError(input));
}
